import { analyticsConstant } from "../Constants/analyticsConstants";
import { HOSPITAL_DATA } from "../Constants/storageConstant.js";
import { getLocalData, getMedicineName, getServiceName } from "../Services/common.js";
import logger from "../Utils/logger.js";
import { Mixpanel } from "./mixPanel.js";
import versionData from "../version.json";

const trackEvent = (eventName, data) => {
  if (process.env.REACT_APP_ENV !== "production") {
    logger.info("EVENTS", eventName, data);
    return;
  }
  Mixpanel.track(eventName, data);
};

export const KEYS = {
  ID: "id",
  CLIENT: "Client",
  HOSPITAL_ID: "Hospital Id",
  HOSPITAL_NAME: "Hospital Name",
  PATIENT_ID: "Patient Id/UHID",
  PATIENT_VISIT_ID: "Patient Visit Id",
  MEDICINE_ID: "Medicine Id",
  MEDICINE_NAME: "Medicine Name",
  MEDICINE_MASTER_ID: "Medicine Master Id",
  SERVICE_ID: "Service Id",
  SERVICE_NAME: "Service Name",
  SERVICE_MASTER_ID: "Service Master Id",
  REPORT_NAME: "Report Name",
  DATE_RANGE: "Date Range",
  REPORT_KEY: "Report Key",
  REPORT_ACTION: "Report Action",
  FORMAT: "Format",
  RESPONSE_TIME_MS: "Response Time (ms)",
  FULL_URL: "Full URL",
  ORIGIN: "ORIGIN",
  ENDPOINT: "ENDPOINT",
  MSG: "message",
  STATUS: "Status",
  SOLD_MEDICINE_NAME: "Sold Medicine Name",
  SEARCH_BY: "Search By",
};

const getDefaultParams = () => {
  const hospitalData = getLocalData(HOSPITAL_DATA);

  return {
    [KEYS.CLIENT]: process.env.REACT_APP_NAME || "local",
    [KEYS.HOSPITAL_NAME]: hospitalData?.name || "-",
    [KEYS.HOSPITAL_ID]: hospitalData?.id || "-",
    [KEYS.VERSION_NUMER]: versionData.lts || "-",
  };
};

export const setUser = (data) => {
  Mixpanel.identify(data.email);
  Mixpanel.people.set({
    [KEYS.ID]: data?.id,
    $name: data?.name,
    role: data?.role,
    $email: data?.email,
    $client: process.env.REACT_APP_NAME || "local",
  });
};

export const trackPatientVisit = (patientId, patientVisitId) => {
  trackEvent(analyticsConstant.PATIENT_VISIT, {
    ...getDefaultParams(),
    [KEYS.PATIENT_ID]: patientId,
    [KEYS.PATIENT_VISIT_ID]: patientVisitId,
  });
};

export const trackMedicineSale = (medicines) => {
  medicines.forEach((medicine) => {
    trackEvent(analyticsConstant.MEDICINE_SALE, {
      ...getDefaultParams(),
      [KEYS.MEDICINE_ID]: medicine.medicine,
      [KEYS.MEDICINE_NAME]: getMedicineName(medicine.medicine),
      [KEYS.MEDICINE_MASTER_ID]: medicine.product_detail_id,
    });
  });
};

export const trackServiceSale = (services) => {
  services.forEach((service) => {
    trackEvent(analyticsConstant.SERVICE_SALE, {
      ...getDefaultParams(),
      [KEYS.SERVICE_ID]: service.service,
      [KEYS.SERVICE_NAME]: getServiceName(service.service),
      [KEYS.SERVICE_MASTER_ID]: service.id,
    });
  });
};

export const trackAadharSearch = () => {
  trackEvent(analyticsConstant.SEARCH, {
    ...getDefaultParams(),
    [KEYS.SEARCH_BY]: "aadhar",
  });
};

export const trackMobileNumberSearch = () => {
  trackEvent(analyticsConstant.SEARCH, {
    ...getDefaultParams(),
    [KEYS.SEARCH_BY]: "mobile",
  });
};

export const trackAlternateMobileNumberSearch = () => {
  trackEvent(analyticsConstant.SEARCH, {
    ...getDefaultParams(),
    [KEYS.SEARCH_BY]: "alternate mobile",
  });
};

export const trackFileNumberSearch = () => {
  trackEvent(analyticsConstant.SEARCH, {
    ...getDefaultParams(),
    [KEYS.SEARCH_BY]: "file number",
  });
};

export const trackOnlineIdSearch = () => {
  trackEvent(analyticsConstant.SEARCH, {
    ...getDefaultParams(),
    [KEYS.SEARCH_BY]: "online id",
  });
};

export const trackPatientStatusFilter = (status) => {
  trackEvent(analyticsConstant.PATIENT_STATUS_FILTER, {
    ...getDefaultParams(),
    [KEYS.STATUS]: status,
  });
};

export const trackPatientSearch = () => {
  trackEvent(analyticsConstant.PATIENT_SEARCH, {
    ...getDefaultParams(),
  });
};

export const trackPatientDateFilter = () => {
  trackEvent(analyticsConstant.PATIENT_DATE_FILTER, {
    ...getDefaultParams(),
  });
};

export const trackSaleReturn = (saleReturnList) => {
  saleReturnList.forEach((sales) => {
    trackEvent(analyticsConstant.SALE_RETURN, {
      ...getDefaultParams(),
      [KEYS.SOLD_MEDICINE_NAME]: getMedicineName(sales.medicine),
    });
  });
};

export const trackReportView = (reportKey, reportName) => {
  let dateRange = "-";
  let reportNameLocal = "-";
  try {
    reportNameLocal = reportName.split("|")[0].trim();
    dateRange = reportName.split("|")[1].trim();
  } catch (error) {
    logger.error("Error splitting report name:", error);
    reportNameLocal = "-";
    dateRange = "-";
  }
  trackEvent(analyticsConstant.REPORT, {
    ...getDefaultParams(),
    [KEYS.REPORT_NAME]: reportNameLocal,
    [KEYS.DATE_RANGE]: dateRange,
    [KEYS.REPORT_KEY]: reportKey,
    [KEYS.REPORT_ACTION]: "view",
  });
};

export const trackReportDownload = (reportKey, reportName, format) => {
  let dateRange = "-";
  let reportNameLocal = "-";
  try {
    reportNameLocal = reportName.split("|")[0].trim();
    dateRange = reportName.split("|")[1].trim();
  } catch (error) {
    logger.error("Error splitting report name:", error);
    reportNameLocal = "-";
    dateRange = "-";
  }
  trackEvent(analyticsConstant.REPORT, {
    ...getDefaultParams(),
    [KEYS.REPORT_NAME]: reportNameLocal,
    [KEYS.DATE_RANGE]: dateRange,
    [KEYS.REPORT_KEY]: reportKey,
    [KEYS.REPORT_ACTION]: "download",
    [KEYS.FORMAT]: format,
  });
};

export const trackAPITime = (time, url, status) => {
  let baseUrl = url;
  let endPoint = url;

  try {
    const parsedUrl = new URL(url);
    endPoint = parsedUrl.pathname.replace("/api", "api") || "-";
    baseUrl = parsedUrl.origin;
  } catch (error) {
    console.error("Error extracting base URL:", error);
  }
  if (time > 900) {
    // if time is greater than 900ms then track the event
    trackEvent(analyticsConstant.API_TIME, {
      ...getDefaultParams(),
      [KEYS.RESPONSE_TIME_MS]: time,
      [KEYS.STATUS]: status,
      [KEYS.FULL_URL]: url,
      [KEYS.ORIGIN]: baseUrl,
      [KEYS.ENDPOINT]: endPoint,
    });
  } else {
    logger.always("API_TIME", {
      [KEYS.RESPONSE_TIME_MS]: time,
      [KEYS.STATUS]: status,
      [KEYS.FULL_URL]: url,
      [KEYS.ORIGIN]: baseUrl,
      [KEYS.ENDPOINT]: endPoint,
    });
  }
};
